import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from 'clsx';
import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useMeasure } from 'react-use';
import Accreditation from '@/components/misc/Accreditation';
import redirectUser from '@/utils/redirectUser';
import useTrackWidgetView from '@/hooks/useTrackWidgetView';
import useOrdinalSections from '@/hooks/useOrdinalWidgetSections';
import logger from '@/utils/logger';
import { defaultFonts } from '@/config/theme';
import { findForm } from '@/utils/reporting/widgetQueries';
import { getEditingWidgetAttr } from '@/components/Editor/hooks/useEditingAreas';
import responsiveUtils from '@/utils/responsive';
import { getCDNWidgetWorkbookUrl } from '@/services/cdn';
import StyleVariables, { getWidgetAccreditationAttr, getWidgetAttr, getWidgetAttrSelector, getWidgetSectionCountAttr } from '../misc/StyleVariables';
import FontsLoader from '../misc/FontsLoader';
import WidgetStyleVariables, { useWidgetStyleVariables } from './WidgetStyleVariables';
import useConfig from './useConfig';
import StandardLayout, { settings as standardLayoutSettings, styles as standardStyles } from './variants/Standard';
import SidebarLayout, { settings as sidebarLayoutSettings, styles as sidebarStyles } from './variants/Sidebar';
import SummaryLayout, { settings as summaryLayoutSettings, styles as summaryStyles } from './variants/Summary';
import NoLayout, { settings as noLayoutSettings, styles as noLayoutStyles } from './variants/None';
import Form from '../Form';
import ThemeProvider from './ThemeProvider';
import settings from './settings';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PREFIX = 'common: components: reporting: Layout:';
const LAYOUT_SETTINGS = {
  standard: standardLayoutSettings,
  sidebarClothesline: sidebarLayoutSettings,
  summary: summaryLayoutSettings,
  none: noLayoutSettings
};
const LAYOUT_STYLES = {
  standard: standardStyles,
  sidebarClothesline: sidebarStyles,
  summary: summaryStyles,
  none: noLayoutStyles
};

const Layout = attrs => {
  const {
    selector = '',
    disabled = false,
    editing = false,
    className,
    activeSectionId,
    updatedAt,
    subscriptionActive,
    fonts: autoLoadFonts,
    onLoad = () => null
  } = attrs;
  const {
    widgetId,
    reportingWidgetId,
    elementId,
    layout,
    config,
    sections,
    spreadsheet,
    workbook,
    workbookMeta
  } = useConfig(selector, attrs, updatedAt);
  const isLoaded = useRef(false);
  const hasSentOnLoad = useRef(false);
  const documentStyles = {};
  const isReportingWidget = widgetId === reportingWidgetId;
  const isAccreditationVisible = subscriptionActive !== true || (config === null || config === void 0 ? void 0 : config.showAccreditation) === true; // Use custom spreadsheet URL from
  // local config, or default to CDN

  const workbookUrl = useMemo(() => spreadsheet || (isReportingWidget ? getCDNWidgetWorkbookUrl(widgetId) : ''), [spreadsheet, widgetId, isReportingWidget]);
  useTrackWidgetView({
    widgetId,
    editing
  });
  useEffect(() => {
    try {
      // Get local REM Base value
      const remBase = getDocumentRemBase();
      if (remBase && remBase > 0) documentStyles.remBase = `${remBase}`; // eslint-disable-next-line
    } catch (err) {} // Trigger container onload event


    requestAnimationFrame(onLoad); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const orderedSections = useOrdinalSections(sections);
  const {
    layout: configLayout = '',
    ctaDestination = ''
  } = config; // Responsive queries

  const [containerEl, {
    width
  }] = useMeasure(); // Send on load notification event

  useEffect(() => {
    if (!isLoaded.current || hasSentOnLoad.current) return;
    hasSentOnLoad.current = true;
    setTimeout(() => requestAnimationFrame(() => onLoad()), 150); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded.current]);
  const handleCtaClick = useCallback(() => {
    if (!ctaDestination) return;

    try {
      redirectUser(ctaDestination);
    } catch (err) {
      logger.error(Error(`${PREFIX} redirect user CTA click failed: ${err}`)); // TODO report configuration error to content author
    }
  }, [ctaDestination]);
  const finalLayout = configLayout || layout;
  const isMediumContainer = useMemo(() => responsiveUtils.isMedium(width), [width]);
  const isLargeContainer = useMemo(() => responsiveUtils.isLarge(width), [width]); // Base settings for selected layout

  const layoutSettings = useMemo(() => {
    return LAYOUT_SETTINGS[finalLayout];
  }, [finalLayout]);
  const layoutStyles = useMemo(() => {
    return LAYOUT_STYLES[finalLayout];
  }, [finalLayout]); // Widget style variables for selected layout

  const {
    globalVariables,
    stylesMainSections,
    stylesSidebarSections
  } = useWidgetStyleVariables({
    isMediumContainer: isMediumContainer,
    isLargeContainer: isLargeContainer,
    config: config,
    baseSettings: settings,
    layoutSettings: layoutSettings
  });
  return /*#__PURE__*/_jsx(ThemeProvider, {
    config: config,
    children: /*#__PURE__*/_jsxs("section", _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
      ref: el => {
        /* @ts-ignore */
        containerEl(el);
        isLoaded.current = true;
      },
      className: clsx(styles.container, className || '')
    }, getWidgetAttr(widgetId)), getWidgetSectionCountAttr(orderedSections.length)), getWidgetAccreditationAttr(isAccreditationVisible)), elementId && {
      id: elementId
    }), updatedAt && {
      'data-update': updatedAt
    }), editing && {
      [getEditingWidgetAttr()]: widgetId
    }), {}, {
      children: [finalLayout === 'standard' ? /*#__PURE__*/_jsx(StandardLayout, {
        config: config,
        editing: editing,
        disabled: disabled,
        activeSectionId: activeSectionId,
        orderedSections: orderedSections,
        widgetId: widgetId,
        reportingWidgetId: reportingWidgetId,
        workbookUrl: workbookUrl,
        workbook: workbook,
        workbookMeta: workbookMeta,
        layout: finalLayout,
        widgetElementId: elementId || '',
        isMediumContainer: isMediumContainer,
        isLargeContainer: isLargeContainer,
        globalVariables: globalVariables,
        stylesMainSections: stylesMainSections,
        handleCtaClick: handleCtaClick
      }) : /*#__PURE__*/_jsx(_Fragment, {}), finalLayout === 'sidebarClothesline' ? /*#__PURE__*/_jsx(SidebarLayout, {
        config: config,
        editing: editing,
        disabled: disabled,
        activeSectionId: activeSectionId,
        orderedSections: orderedSections,
        widgetId: widgetId,
        reportingWidgetId: reportingWidgetId,
        widgetElementId: elementId || '',
        workbookUrl: workbookUrl,
        workbook: workbook,
        workbookMeta: workbookMeta,
        isMediumContainer: isMediumContainer,
        isLargeContainer: isLargeContainer,
        globalVariables: globalVariables,
        stylesMainSections: stylesMainSections,
        stylesSidebarSections: stylesSidebarSections,
        handleCtaClick: handleCtaClick
      }) : /*#__PURE__*/_jsx(_Fragment, {}), finalLayout === 'summary' ? /*#__PURE__*/_jsx(SummaryLayout, {
        config: config,
        editing: editing,
        disabled: disabled,
        activeSectionId: activeSectionId,
        orderedSections: orderedSections,
        widgetId: widgetId,
        reportingWidgetId: reportingWidgetId,
        widgetElementId: elementId || '',
        workbookUrl: workbookUrl,
        workbook: workbook,
        workbookMeta: workbookMeta,
        isMediumContainer: isMediumContainer,
        isLargeContainer: isLargeContainer,
        globalVariables: globalVariables,
        stylesMainSections: stylesMainSections,
        stylesSidebarSections: stylesSidebarSections,
        handleCtaClick: handleCtaClick
      }) : /*#__PURE__*/_jsx(_Fragment, {}), finalLayout === 'none' ? /*#__PURE__*/_jsx(NoLayout, {
        config: config,
        editing: editing,
        disabled: disabled,
        orderedSections: orderedSections,
        activeSectionId: activeSectionId,
        widgetId: widgetId,
        reportingWidgetId: reportingWidgetId,
        widgetElementId: elementId || '',
        workbookUrl: workbookUrl,
        workbook: workbook,
        workbookMeta: workbookMeta,
        isMediumContainer: isMediumContainer,
        isLargeContainer: isLargeContainer,
        globalVariables: globalVariables,
        stylesMainSections: stylesMainSections,
        handleCtaClick: handleCtaClick
      }) : /*#__PURE__*/_jsx(_Fragment, {}), isReportingWidget && !findForm(sections) ? /*#__PURE__*/_jsx(Form, {
        config: {
          questions: [],
          groups: [],
          saveSession: false
        },
        sectionId: "zero",
        widgetId: widgetId,
        reportingWidgetId: reportingWidgetId,
        widgetElementId: elementId || '',
        workbookUrl: workbookUrl,
        workbook: workbook,
        workbookMeta: workbookMeta,
        sectionsIndex: Object.keys(sections).length,
        noFormHTML: true,
        disabled: disabled,
        editing: editing
      }) : /*#__PURE__*/_jsx(_Fragment, {}), isAccreditationVisible ? /*#__PURE__*/_jsx(Accreditation, {
        editing: editing
      }) : /*#__PURE__*/_jsx(_Fragment, {}), /*#__PURE__*/_jsx(FontsLoader, {
        loadFonts: autoLoadFonts || defaultFonts
      }), /*#__PURE__*/_jsx(StyleVariables, {
        selector: getWidgetAttrSelector(widgetId),
        data: _objectSpread(_objectSpread(_objectSpread({}, settings.stylesTheme), config.stylesTheme), documentStyles)
      }), /*#__PURE__*/_jsx(WidgetStyleVariables, {
        widgetId: widgetId,
        globalSettings: globalVariables,
        mainSectionsSettings: stylesMainSections,
        sidebarSectionsSettings: stylesSidebarSections,
        styles: layoutStyles
      })]
    }))
  });
};

export default Layout; // Get computed rem base from local document

function getDocumentRemBase() {
  if (false) return 0;
  return parseFloat(window.getComputedStyle(document.documentElement).fontSize);
}